import { z } from 'zod';

import { toZonedDateKey } from '@nucleus-commerce/core';

/**
 * The analytics page shows the last 7, 28 or 90 complete days in the store's timezone. The
 * nightly snapshot writes yesterday's row, so a range ends yesterday: a window ending today
 * would always close on an empty day. Day arithmetic runs on UTC midnights so it never
 * crosses a DST edge.
 */

export const ANALYTICS_RANGES = [7, 28, 90] as const;
export type AnalyticsRangeDays = (typeof ANALYTICS_RANGES)[number];
export const DEFAULT_ANALYTICS_RANGE: AnalyticsRangeDays = 28;

/**
 * URL search of the page (`?days=7`). The key is not `range`: the reports routes own that name
 * with string presets, and every route's search shape meets in one router-wide union. Anything
 * malformed lands on the default range.
 */
export const analyticsSearchSchema = z.object({
  days: z.literal(ANALYTICS_RANGES).optional().catch(undefined),
});

export type AnalyticsSearch = z.infer<typeof analyticsSearchSchema>;

export interface AnalyticsRange {
  days: AnalyticsRangeDays;
  from: string;
  to: string;
}

const DAY_MS = 86_400_000;
const parseDay = (day: string) => Date.parse(`${day}T00:00:00Z`);
const formatDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function addDays(day: string, days: number): string {
  return formatDay(parseDay(day) + days * DAY_MS);
}

export function todayIn(timeZone: string, now: Date = new Date()): string {
  return toZonedDateKey(now, timeZone);
}

export function isAnalyticsRange(value: unknown): value is AnalyticsRangeDays {
  return ANALYTICS_RANGES.some((days) => days === value);
}

/** `days` complete days ending yesterday (store calendar). */
export function resolveAnalyticsRange(search: AnalyticsSearch, today: string): AnalyticsRange {
  const days = search.days ?? DEFAULT_ANALYTICS_RANGE;
  const to = addDays(today, -1);
  return { days, from: addDays(to, -(days - 1)), to };
}

export type Change = { kind: 'none' } | { kind: 'up' | 'down' | 'flat'; ratio: number };

/**
 * Change of a figure against the previous period. A zero previous value has no meaningful
 * percentage, so it reads as "no previous data" rather than +∞%.
 */
export function computeChange(current: number, previous: number): Change {
  if (previous === 0) return { kind: 'none' };
  const ratio = (current - previous) / Math.abs(previous);
  if (ratio > 0) return { kind: 'up', ratio };
  if (ratio < 0) return { kind: 'down', ratio: Math.abs(ratio) };
  return { kind: 'flat', ratio: 0 };
}
